import {
    type IPublicClientApplication,
    type Configuration,
    type PerformanceTelemetryReporterConfiguration,
    type BrowserAuthOptions,
    type CacheOptions,
    type BrowserSystemOptions,
    PublicClientApplication,
    createNestablePublicClientApplication,
    LogLevel,
    BrowserCacheLocation,
    ProtocolMode,
} from '@azure/msal-browser-1p';
import { isBootFeatureEnabled } from 'owa-metatags/lib/isBootFeatureEnabled';
import { getClientVersion, isHosted } from 'owa-config';
import { getOrigin } from 'owa-url/lib/getOrigin';
import { trace } from 'owa-trace';
import { getCommonAuthorityUrlFromMetaTag } from './utils/getAuthority';
import { OwaApplicationId } from 'owa-service/lib/constants/ApplicationId';
import { isBusinessDomain } from './isMsalEnabledForBusiness';
import { isConsumerDomain } from './isMsalEnabledForConsumerDomain';

let msalInstancePromise: Promise<IPublicClientApplication> | null = null;
let appSpecificConfiguration: AppSpecificConfiguration | undefined = undefined;

interface CustomGlobalThis {
    __JSWRAPPER?: boolean; // This is a flag set by MCAS
}

declare var globalThis: CustomGlobalThis;

export function getMsalInstance(): Promise<IPublicClientApplication> {
    // NOTE: getMsalInstance will only work after a call (during boot) to createMsalInstance which
    // configures MSAL with the correct clientId. If someone tries to use MSAL before creation, then throw
    // this error to let the developer know that MSAL is being called too early, or the boot flight is disabled.
    return msalInstancePromise ?? Promise.reject(new Error('MSAL instance not available'));
}

export type AppSpecificConfiguration = {
    auth: Pick<BrowserAuthOptions, 'clientId' | 'redirectUri' | 'protocolMode'>;
    cache?: Pick<CacheOptions, 'cacheLocation'>;

    /**
     * Set to true to indicate that this app should NOT fallback to other host token mechanisms
     * (ie. Hub SDK) if NAA is unsupported in the host. Used if you require use of your clientId
     * and don't have an existing security-approved/working solution that relies on full trust auth
     */
    disableGetAuthTokenFallback?: boolean;

    /**
     * If set, indicates the URI scheme fragment to use when constructing the broker redirect URI.
     */
    nestedAppAuthScheme?: string;
};

// To do: Need to modify this funtion to return config for different environments.
export function createMsalInstance(appConfig?: AppSpecificConfiguration): void {
    if (msalInstancePromise) {
        // NOTE: this function should only be called once during boot with a specific clientId. To prevent
        // other callers from calling init with potentially different parameters, and to retain control
        // of when MSAL init occurs and use of the correct clientId, throw an error here. This should not
        // be hit during normal runtime and execution, and is to let client developers know.
        throw new Error('MSAL instance creation is managed by boot code');
    }

    appSpecificConfiguration = appConfig;

    const supportsNestedAppAuth = isHosted();

    const msalConfig: Configuration = {
        auth: {
            clientId: OwaApplicationId,
            redirectUri: `${getOrigin()}/mail/`,
            authority: getCommonAuthorityUrlFromMetaTag(),
            navigateToLoginRequestUrl: true,
            clientCapabilities: ['CP1'],
            ...appSpecificConfiguration?.auth,
            supportsNestedAppAuth,
        },
        cache: {
            cacheLocation: BrowserCacheLocation.LocalStorage,
            ...appSpecificConfiguration?.cache,
        },
        system: {
            loggerOptions: getLoggerOptions(),
            allowPlatformBroker: false,
            allowRedirectInIframe: true,
        },
    };

    msalConfig.auth.protocolMode =
        appConfig?.auth.protocolMode ??
        (isTdsOidcAuthority(msalConfig.auth.authority)
            ? ProtocolMode.OIDC
            : msalConfig.auth.clientId == OwaApplicationId &&
              !supportsNestedAppAuth && // Only enable EAR for non-hosted scenarios
              ((isBootFeatureEnabled('auth-msaljs-enableEAR-enterprise') && isBusinessDomain()) ||
                  (isBootFeatureEnabled('auth-msaljs-enableEAR-consumer') && isConsumerDomain()))
            ? ProtocolMode.EAR
            : ProtocolMode.AAD);

    // Add knownAuthorities for TDS OIDC server to bypass authority validation
    if (isTdsOidcAuthority(msalConfig.auth.authority)) {
        const authorityUrl = new URL(msalConfig.auth.authority ?? '');
        msalConfig.auth.knownAuthorities = [authorityUrl.host];
    }

    // When MCAS session starts, MCAS puts __JSWRAPPER into the window global var. If this is present, we should disable CAE capabilities.
    if (globalThis.__JSWRAPPER || isBootFeatureEnabled('auth-msaljs-disableCaeCapabilities')) {
        msalConfig.auth.clientCapabilities = undefined;
    }

    // Configure Telemetry for MATS integration
    const telemConfig: PerformanceTelemetryReporterConfiguration = {
        appName: 'OWA',
        appVersion: getClientVersion(),
    };

    msalInstancePromise = supportsNestedAppAuth
        ? createNestablePublicClientApplication(msalConfig)
        : PublicClientApplication.createPublicClientApplication(msalConfig, telemConfig);

    // purposefully don't await the promise, as any consumers will await getMsalInstance when
    // they'll first need it, so no need to artificially do it ahead of time
}

// Get the client id of the application creating MSAL instance.
export function getClientId(): string {
    return appSpecificConfiguration?.auth.clientId ?? OwaApplicationId;
}

//Get the redirect uri where authentication responses can be received by the app.
export function getRedirectUri(): string {
    return appSpecificConfiguration?.auth.redirectUri ?? `${getOrigin()}/mail/`;
}

// Get the protocol mode used by the application.
export function getProtocolMode(): ProtocolMode | undefined {
    return appSpecificConfiguration?.auth.protocolMode ?? undefined;
}

export function getNestedAppAuthScheme(): string | undefined {
    return appSpecificConfiguration?.nestedAppAuthScheme;
}

function getLoggerOptions(): BrowserSystemOptions['loggerOptions'] | undefined {
    if (isBootFeatureEnabled('auth-msaljs-EnableLogging')) {
        return {
            logLevel: LogLevel.Trace,
            loggerCallback: (level: LogLevel, message: string, containsPii: boolean) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        trace.warn(message);
                        return;
                    case LogLevel.Info:
                        trace.info(message);
                        return;
                    case LogLevel.Verbose:
                        trace.verbose(message);
                        return;
                    case LogLevel.Warning:
                        trace.warn(message);
                        return;
                    default:
                        trace.info(message);
                        return;
                }
            },
        };
    }
    return undefined;
}

function isTdsOidcAuthority(authority: string | undefined): boolean {
    if (!authority) {
        return false;
    }
    try {
        const url = new URL(authority);
        return url.pathname.startsWith('/owa/tds/openid');
    } catch {
        return false;
    }
}
